/**
 * Composite SSE → TanStack Query invalidation bridge for the campaign-detail
 * surface. Wires all events that both admin and brand pages need:
 *
 *   1. new_message / unread_count → invalidate conversation + roster tabs
 *   2. roster_* events            → useCampaignSSE (per-event key precision)
 *   3. ai_sentiment_review_ready  → invalidate sentiment tab + live-content
 *                                   tab + creator-content tab so per-row
 *                                   sentiment scores refresh as soon as AI's
 *                                   Phase B aggregate completes
 *   4. ig_profile_updated         → invalidate roster tabs for this campaign
 *
 * campaign_live_views_trend_updated is handled inside useCampaignSSE — no
 * duplicate useCampaignTrendSSE needed here.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useThreadsSSE, useAiReviewSSE } from '@discovr/common';
import { campaignKeys } from '../queryKeys';
import { useCampaignSSE } from './useCampaignSSE';
import { patchRosterEntry, bumpRosterEntryToTop, TAB_FOR_THREAD } from './rosterCacheOps';

export function useCampaignDetailSSE(campaignId: string) {
  const queryClient = useQueryClient();

  // Predicate matching all roster tab queries for this campaign.
  // Roster key shape: ['roster', campaignId, tab, filters] — campaignId is at [1].
  const rosterPredicate = (query: { queryKey: readonly unknown[] }) => {
    const key = query.queryKey;
    return Array.isArray(key) && key[0] === 'roster' && key[1] === campaignId;
  };

  useThreadsSSE({
    enabled: !!campaignId,
    onNewMessage: (payload) => {
      if (!payload.entry_id) return;
      // Prefix-invalidate all thread types for this entry (selection/script/content).
      // Actual key: campaignKeys.conversation(entryId, type) = ['conversation', entryId, type].
      queryClient.invalidateQueries({ queryKey: ['conversation', payload.entry_id] });
      // Optimistic move-to-top so the row jumps instantly (WhatsApp-style) instead
      // of after the refetch round-trip — scoped to the tab that owns this thread
      // (selection→creators, script→scripts, content→content) so we don't reorder
      // a tab whose recency key this message didn't bump. The invalidate reconciles
      // the authoritative last_activity_at order + cross-page/cross-tab promotion.
      const tab = payload.thread_type ? TAB_FOR_THREAD[payload.thread_type] : undefined;
      bumpRosterEntryToTop(queryClient, campaignId, payload.entry_id, tab);
      queryClient.invalidateQueries({ predicate: rosterPredicate });
    },
    onMessageMutated: (payload) => {
      if (!payload.entry_id) return;
      // Same re-fetch as a new message — the open thread must stop showing an
      // edited/deleted message — but deliberately NO bumpRosterEntryToTop: the
      // BE does not advance last_activity_at on edit/delete, so an optimistic
      // move-to-top would jump the row and then snap it back on refetch.
      queryClient.invalidateQueries({ queryKey: ['conversation', payload.entry_id] });
      queryClient.invalidateQueries({ predicate: rosterPredicate });
    },
    onUnreadCount: (payload) => {
      // Paint the row's unread badge instantly from the exact count the BE sends,
      // rather than waiting for a roster refetch (which could be deduped/stale-
      // gated and drop the increment, leaving the badge missing). Handles both
      // the incoming-message increment and the mark-read echo (count → 0). Each
      // tab's unread_count is a DIFFERENT thread's count, so patch ONLY the tab
      // that owns this thread_type — else a script/content event clobbers the
      // Creators-tab selection badge for the same entry.
      if (!payload.entry_id || !payload.thread_type) return;
      patchRosterEntry(
        queryClient,
        campaignId,
        payload.entry_id,
        (e) => ({ ...e, unread_count: payload.unread_count ?? 0 }),
        TAB_FOR_THREAD[payload.thread_type],
      );
    },
  });

  useCampaignSSE(campaignId);

  // ai_script_review_ready / ai_content_review_ready — refresh the OPEN review
  // drawer's per-entry detail for EVERY viewer. Previously only the brand page
  // bumped a per-browser-tab store version, so other viewers (and the admin
  // app) never saw the AI envelope land. This hook is mounted by both apps, so
  // invalidating scriptDetail/contentDetail(entry_id) here makes the AI
  // analysis populate live everywhere; the brand page's store bump still works
  // as a complementary fast-path.
  useAiReviewSSE<{ entry_id?: string; campaign_id?: string }>({
    eventName: 'ai_script_review_ready',
    enabled: !!campaignId,
    // Scope to this campaign (sseManager is a global per-user singleton, so a
    // job completing on a different campaign would otherwise fire here). The
    // dashboard-path event omits campaign_id/entry_id and is correctly ignored.
    filter: (p) => !!p.campaign_id && p.campaign_id === campaignId,
    onReady: (p) => {
      if (p.entry_id) queryClient.invalidateQueries({ queryKey: campaignKeys.scriptDetail(p.entry_id) });
    },
  });

  useAiReviewSSE<{ entry_id?: string; campaign_id?: string }>({
    eventName: 'ai_content_review_ready',
    enabled: !!campaignId,
    filter: (p) => !!p.campaign_id && p.campaign_id === campaignId,
    onReady: (p) => {
      if (p.entry_id) queryClient.invalidateQueries({ queryKey: campaignKeys.contentDetail(p.entry_id) });
    },
  });

  // ai_geo_finalize_ready — the GEO finalize pack (ContentDetail.geo_finalize)
  // is generated async, auto-enqueued by the BE once both prerequisites land
  // (script GEO pack + content review), so there is no client-triggered
  // fetch to await. This single event covers BOTH the success and the
  // terminal-failure webhook branches (no separate "failed" event on the
  // wire — see useAiReviewSSE's AiReviewEventName doc comment); either way
  // a refetch is exactly right, since the envelope's own `status`
  // (complete/failed) tells ContentResultTabs' GEO tab which state to render.
  useAiReviewSSE<{ entry_id?: string; campaign_id?: string }>({
    eventName: 'ai_geo_finalize_ready',
    enabled: !!campaignId,
    filter: (p) => !!p.campaign_id && p.campaign_id === campaignId,
    onReady: (p) => {
      if (p.entry_id) queryClient.invalidateQueries({ queryKey: campaignKeys.contentDetail(p.entry_id) });
    },
  });

  useAiReviewSSE<{ campaign_id?: string }>({
    eventName: 'ai_sentiment_review_ready',
    enabled: !!campaignId,
    filter: (p) => p.campaign_id === campaignId,
    onReady: () => {
      // Sentiment tab — the dedicated chart + creator breakdown.
      queryClient.invalidateQueries({ queryKey: campaignKeys.sentimentReports(campaignId) });
      // Live-content tab — per-row sentiment scores (sentiment_index, pos_pct,
      // risk_index, trend_7d) come from the same BE endpoint and must refresh
      // as soon as AI's Phase B aggregate completes, not just on the next cron tick.
      queryClient.invalidateQueries({ queryKey: campaignKeys.rosterTab(campaignId, 'live-content') });
      // Creator-content tab — same per-row sentiment columns.
      queryClient.invalidateQueries({ queryKey: campaignKeys.creatorContentReports(campaignId) });
    },
  });

  // ig_profile_updated — fired by the profile worker after followers, verified
  // flag, and profile picture are persisted. Invalidates all roster tab queries
  // for this campaign so avatars and follower counts refresh without a reload.
  useAiReviewSSE<{ campaign_id?: string; entry_id?: string }>({
    eventName: 'ig_profile_updated',
    enabled: !!campaignId,
    filter: (p) => p.campaign_id === campaignId,
    onReady: () => {
      queryClient.invalidateQueries({ predicate: rosterPredicate });
    },
  });
}
